import {
  ApiError,
  ChartPeriodDto,
  CreateDerivativesOrderRequest,
  CustomerPositionMode,
  DerivativesChartType,
  DerivativesMarginMode,
  DerivativesOpenedPositionProjection,
  DerivativesOrderProjection,
  DerivativesOrderStatus,
  DerivativesPositionMode,
  SetCustomerTradingPairLeverageRequest,
  SetCustomerTradingPairMarginModeRequest,
  SetDerivativesPositionModeRequest,
  TradingGatewayApiClient,
  UpdateDerivativesOrderRequest,
  UpdateDerivativesPositionMarginRequest,
  UpdateDerivativesPositionRequest,
} from 'common/api/autogenerated/trading-gateway';

import { tempFuturesConfigurator } from 'views/derivatives/utils/temp-config';

import { FUTURES_ERRORS } from '../dashboard/components/OrderForm/constants';

const tradingGatewayApiClient = new TradingGatewayApiClient({
  BASE: '/trading-gateway',
  WITH_CREDENTIALS: true,
});

export const getDerivatives = () =>
  tradingGatewayApiClient.derivativesTradingPairs.getV1DerivativesTradingPairs(undefined, undefined, undefined, 500);

export const getDerivativesGroups = () =>
  tradingGatewayApiClient.derivativesTradingPairs.getV1DerivativesTradingPairsGroups();

export const getFavoriteDerivatives = () =>
  tradingGatewayApiClient.derivativesTradingPairs.getV1DerivativesTradingPairsFavorites();

export const setFavoriteDerivatives = (symbol: string) =>
  tradingGatewayApiClient.derivativesTradingPairs.postV1DerivativesTradingPairsFavorites(symbol);

export const deleteFavoriteDerivatives = (symbol: string) =>
  tradingGatewayApiClient.derivativesTradingPairs.deleteV1DerivativesTradingPairsFavorites(symbol);

export const getDerivativeInfo = (symbol: string) =>
  tradingGatewayApiClient.derivativesTradingPairs.getV1DerivativesTradingPairs1(symbol);

export const getDerivativeChartCandles = (
  tradingPairCode: string[],
  candlesCount: number,
  to: string,
  timeFrame: ChartPeriodDto,
  type = DerivativesChartType.LAST,
) =>
  tradingGatewayApiClient.derivativesTradingPairs.getV1DerivativesTradingPairsCharts(
    tradingPairCode,
    candlesCount,
    timeFrame,
    undefined,
    to,
    type,
  );

export const getDerivativesDailyCandles = (symbol: string[]) =>
  tradingGatewayApiClient.derivativesTradingPairs.getV1DerivativesTradingPairsCharts(
    symbol,
    23,
    ChartPeriodDto.H1,
    undefined,
    new Date().toISOString(),
  );

export const getDerivativeOpenOrders = (settlementCurrency?: string) =>
  tradingGatewayApiClient.derivativesOrders.getV1DerivativesOrders(
    undefined,
    [DerivativesOrderStatus.PENDING, DerivativesOrderStatus.PARTIALLY_EXECUTED],
    undefined,
    settlementCurrency,
  );

export const getDerivativeClosedOrders = (count?: number) =>
  tradingGatewayApiClient.derivativesOrders.getV1DerivativesOrders(
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    count,
  );

export const deleteDerivativeOrder = (id: DerivativesOrderProjection['id'] = 0) =>
  tradingGatewayApiClient.derivativesOrders.deleteV1DerivativesOrders(id);

export const editDerivativeOrder = (id: DerivativesOrderProjection['id'], order: UpdateDerivativesOrderRequest) =>
  tradingGatewayApiClient.derivativesOrders.putV1DerivativesOrders(id ?? 0, order);

export const getDerivativeOpenPositions = () =>
  tradingGatewayApiClient.derivativesOpenedPositions.getV1DerivativesOpenedPositions();

export const getDerivativesHistory = (code?: string) =>
  tradingGatewayApiClient.derivativesClosedPositions.getV1DerivativesClosedPositions(code);

export const getDerivativeFormConfiguration = (code: string) =>
  tradingGatewayApiClient.derivativesCustomerConfiguration.getV1DerivativesCustomerConfigurationTradingPairs(code);

export const getDerivativePositionMode = () =>
  Promise.resolve<CustomerPositionMode>({
    positionMode: DerivativesPositionMode.HEDGE,
  });
// 85523 TEMP COMMENTED
// tradingGatewayApiClient.derivativesCustomerConfiguration.getV1DerivativesCustomerConfigurationPositionMode();

const handleNotYetCreatedFutureWalletError = (
  error: unknown,
  code: string,
  value:
    | SetCustomerTradingPairLeverageRequest
    | SetDerivativesPositionModeRequest
    | SetCustomerTradingPairMarginModeRequest,
) => {
  const errorCode = FUTURES_ERRORS.FU005;
  const errorMessage = 'Customer login was not created';
  if (
    error instanceof ApiError &&
    (error?.body?.message?.toLowerCase().includes(errorMessage.toLowerCase()) || error?.body?.code === errorCode)
  ) {
    console.log(`Intercepted not yet created future wallet error - "${errorMessage}"`);
    tempFuturesConfigurator.update(code, value);
    return;
  }
  throw error;
};

export const putDerivativePositionMode = (
  _code: string,
  _positionMode: DerivativesPositionMode,
  _handleNotYetCreatedFutureWalletError = true,
  // eslint-disable-next-line arrow-body-style
) => {
  return Promise.resolve();

  // 85523 TEMP COMMENTED
  // return tradingGatewayApiClient.derivativesCustomerConfiguration
  //   .putV1DerivativesCustomerConfigurationPositionMode({
  //     positionMode,
  //   })
  //   .catch((e) => {
  //     if (!_handleNotYetCreatedFutureWalletError) throw e;
  //     return handleNotYetCreatedFutureWalletError(e, code, { positionMode });
  //   });
};

export const putDerivativeLeverage = (code: string, leverage: number, _handleNotYetCreatedFutureWalletError = true) =>
  tradingGatewayApiClient.derivativesCustomerConfiguration
    .putV1DerivativesCustomerConfigurationTradingPairsLeverage(code, {
      leverage,
    })
    .catch((e) => {
      if (!_handleNotYetCreatedFutureWalletError) throw e;
      return handleNotYetCreatedFutureWalletError(e, code, { leverage });
    });

export const putDerivativeMarginMode = (
  code: string,
  marginMode: DerivativesMarginMode,
  _handleNotYetCreatedFutureWalletError = true,
) =>
  tradingGatewayApiClient.derivativesCustomerConfiguration
    .putV1DerivativesCustomerConfigurationTradingPairsMarginMode(code, {
      marginMode,
    })
    .catch((e) => {
      if (!_handleNotYetCreatedFutureWalletError) throw e;
      return handleNotYetCreatedFutureWalletError(e, code, { marginMode });
    });

export const postDerivativeOrder = (order: CreateDerivativesOrderRequest) =>
  tradingGatewayApiClient.derivativesOrders.postV1DerivativesOrders(order);

export const putDerivativeOrder = (id: number, order: UpdateDerivativesOrderRequest) =>
  tradingGatewayApiClient.derivativesOrders.putV1DerivativesOrders(id, order);

export const putDerivativePositionMargin = (
  id: DerivativesOpenedPositionProjection['id'],
  data: UpdateDerivativesPositionMarginRequest,
) => tradingGatewayApiClient.derivativesOpenedPositions.putV1DerivativesOpenedPositionsMargin(id ?? 0, data);

export const putDerivativePosition = (
  id: DerivativesOpenedPositionProjection['id'],
  data: UpdateDerivativesPositionRequest,
) => tradingGatewayApiClient.derivativesOpenedPositions.putV1DerivativesOpenedPositions(id ?? 0, data);

export const getInOrdersOpenPositions = (settlementCurrency: string) =>
  tradingGatewayApiClient.derivativesOpenedPositions.getV1DerivativesOpenedPositions(undefined, settlementCurrency);

export const getInOrdersOpenOrders = (settlementCurrency: string) => getDerivativeOpenOrders(settlementCurrency);
